import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Loader2, Save, Bell, RefreshCw } from 'lucide-react'
import { MFASection } from '@/components/settings/MFASection'
import { PasswordField } from '@/components/ui/password-field'
import { toast } from '@atrvd/saas-ui'
import {
  profileSchema,
  syncSettingsSchema,
  changePasswordSchema,
  type ProfileInput,
  type SyncSettingsInput,
  type ChangePasswordInput,
} from '@dcp/shared'
import {
  useProfile,
  useUpdateProfile,
  useSyncSettings,
  useUpdateSyncSettings,
  useChangePassword,
} from '@/hooks/useProfile'
import {
  Button,
  Input,
  Label,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Separator,
  Alert,
  AlertDescription,
  PageHeader,
  Toggle,
} from '@atrvd/saas-ui'

/**
 * Profile information form section
 * Allows users to update their name and company information
 */
function ProfileSection() {
  const { data: profile, isLoading } = useProfile()
  const updateProfile = useUpdateProfile()

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<ProfileInput>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      fullName: '',
    },
  })

  useEffect(() => {
    if (profile) {
      reset({
        fullName: profile.fullName || '',
      })
    }
  }, [profile, reset])

  const onSubmit = async (data: ProfileInput) => {
    try {
      await updateProfile.mutateAsync(data)
      toast.success('Profile updated successfully')
    } catch (error) {
      toast.error('Failed to update profile')
    }
  }

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex h-32 items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Profile</CardTitle>
        <CardDescription>Update your personal information</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="fullName">Full Name</Label>
              <Input
                id="fullName"
                placeholder="John Doe"
                {...register('fullName')}
              />
              {errors.fullName && (
                <p className="text-sm text-destructive">{errors.fullName.message}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={profile?.email || ''}
                disabled
                className="bg-muted"
              />
              <p className="text-xs text-muted-foreground">
                Email cannot be changed
              </p>
            </div>
          </div>

          <Button type="submit" disabled={!isDirty || updateProfile.isPending}>
            {updateProfile.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Save className="mr-2 h-4 w-4" />
            )}
            Save Changes
          </Button>
        </form>
      </CardContent>
    </Card>
  )
}

/**
 * Sync settings configuration section
 * Allows users to configure automatic transaction synchronization
 */
function SyncSettingsSection() {
  const { data: settings, isLoading } = useSyncSettings()
  const updateSettings = useUpdateSyncSettings()

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { isDirty },
  } = useForm<SyncSettingsInput>({
    resolver: zodResolver(syncSettingsSchema),
    defaultValues: {
      autoSync: false,
      syncInterval: 'daily',
      notifyOnError: true,
      notifyOnSuccess: false,
    },
  })

  useEffect(() => {
    if (settings) {
      reset(settings)
    }
  }, [settings, reset])

  const onSubmit = async (data: SyncSettingsInput) => {
    try {
      await updateSettings.mutateAsync(data)
      toast.success('Sync settings updated')
    } catch (error) {
      toast.error('Failed to update settings')
    }
  }

  const autoSync = watch('autoSync')

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex h-32 items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <RefreshCw className="h-5 w-5" />
          Sync Settings
        </CardTitle>
        <CardDescription>Configure automatic synchronization</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5 flex-1">
              <p className="text-sm font-medium">Auto-sync transactions</p>
              <p className="text-sm text-muted-foreground">
                Automatically sync new transactions
              </p>
            </div>
            <Toggle
              id="autoSync"
              checked={watch('autoSync')}
              onChange={(checked) => setValue('autoSync', checked, { shouldDirty: true })}
            />
          </div>

          {autoSync && (
            <div className="space-y-2 pl-4 border-l-2 border-muted">
              <Label htmlFor="syncInterval">Sync Interval</Label>
              <select
                id="syncInterval"
                className="flex h-10 w-full max-w-xs rounded-md border border-input bg-background px-3 py-2 text-sm"
                {...register('syncInterval')}
              >
                <option value="hourly">Every hour</option>
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
              </select>
            </div>
          )}

          <Separator />

          <div className="space-y-4">
            <h4 className="text-sm font-medium flex items-center gap-2">
              <Bell className="h-4 w-4" />
              Notifications
            </h4>

            <div className="flex items-center justify-between">
              <div className="space-y-0.5 flex-1">
                <p className="text-sm font-medium">Notify on errors</p>
                <p className="text-sm text-muted-foreground">
                  Receive email alerts when sync fails
                </p>
              </div>
              <Toggle
                id="notifyOnError"
                checked={watch('notifyOnError')}
                onChange={(checked) => setValue('notifyOnError', checked, { shouldDirty: true })}
              />
            </div>

            <div className="flex items-center justify-between">
              <div className="space-y-0.5 flex-1">
                <p className="text-sm font-medium">Notify on success</p>
                <p className="text-sm text-muted-foreground">
                  Receive email after successful sync
                </p>
              </div>
              <Toggle
                id="notifyOnSuccess"
                checked={watch('notifyOnSuccess')}
                onChange={(checked) => setValue('notifyOnSuccess', checked, { shouldDirty: true })}
              />
            </div>
          </div>

          <Button type="submit" disabled={!isDirty || updateSettings.isPending}>
            {updateSettings.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Save className="mr-2 h-4 w-4" />
            )}
            Save Settings
          </Button>
        </form>
      </CardContent>
    </Card>
  )
}

/**
 * Password change form section
 * Allows users to update their account password
 */
function ChangePasswordSection() {
  const changePassword = useChangePassword()

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<ChangePasswordInput>({
    resolver: zodResolver(changePasswordSchema),
  })

  // Watch newPassword so the strength meter / toggle visibility reflect the
  // true form value, including after reset() clears the form.
  const newPasswordValue = watch('newPassword') ?? ''

  const onSubmit = async (data: ChangePasswordInput) => {
    try {
      // Note: currentPassword is collected for UX but Supabase Auth doesn't verify it
      // for already authenticated users - it directly updates the password
      await changePassword.mutateAsync({
        newPassword: data.newPassword,
      })
      toast.success('Password changed successfully')
      reset()
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to change password'
      toast.error(message)
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Change Password</CardTitle>
        <CardDescription>Update your account password</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <PasswordField
            id="currentPassword"
            label="Current Password"
            placeholder="Enter your current password"
            autoComplete="current-password"
            error={errors.currentPassword?.message}
            {...register('currentPassword')}
          />

          <PasswordField
            id="newPassword"
            label="New Password"
            autoComplete="new-password"
            error={errors.newPassword?.message}
            showStrengthMeter
            value={newPasswordValue}
            {...register('newPassword')}
          />

          <PasswordField
            id="confirmPassword"
            label="Confirm New Password"
            placeholder="Confirm your new password"
            autoComplete="new-password"
            error={errors.confirmPassword?.message}
            {...register('confirmPassword')}
          />

          <Button type="submit" disabled={changePassword.isPending}>
            {changePassword.isPending && (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            )}
            Change Password
          </Button>
        </form>
      </CardContent>
    </Card>
  )
}

/**
 * Danger zone section with destructive actions
 * Contains irreversible account deletion functionality
 */
function DangerZoneSection() {
  return (
    <Card className="border-destructive/50">
      <CardHeader>
        <CardTitle className="text-destructive">Danger Zone</CardTitle>
        <CardDescription>Irreversible and destructive actions</CardDescription>
      </CardHeader>
      <CardContent>
        <Alert variant="destructive">
          <AlertDescription className="flex items-center justify-between">
            <div>
              <p className="font-medium">Delete Account</p>
              <p className="text-sm opacity-90">
                Permanently delete your account and all associated data
              </p>
            </div>
            <Button variant="destructive" size="sm">
              Delete Account
            </Button>
          </AlertDescription>
        </Alert>
      </CardContent>
    </Card>
  )
}

/**
 * Profile settings page
 * Contains user profile, sync settings, password change, and danger zone
 */
export function SettingsProfilePage() {
  return (
    <div className="space-y-6">
      <PageHeader
        title="Profile Settings"
        description="Manage your account and preferences"
      />

      <div className="grid gap-6">
        <ProfileSection />
        <MFASection />
        <SyncSettingsSection />
        <ChangePasswordSection />
        <DangerZoneSection />
      </div>
    </div>
  )
}